import { useEffect } from 'react';

import { Command } from '@dreryk/command-query-api';

import { Bean, PromiseVoidFunction, TranslatableText } from '@dreryk/react-common';
import { Anchor, List } from '@dreryk/react-components';

import { ExtractItemId, FormsTemplateListItem as FormsTemplateListItemType } from '../FormsTemplates.types';
import { useFormsTemplatesContext } from '../formsTemplatesContext';
import { useFormsTemplatesScrollContext } from '../scrollContext/formsTemplatesScrollContext';

import { FormsTemplatesListItem } from './FormsTemplatesListItem';
import { isItemActive } from './FormsTemplatesListItem.utils';

export const EMPTY_TEMPLATE_ID = 'added';

type FormsTemplatesListItemsRendererProps<ITEM extends Record<string, unknown>> = {
   readonly items: FormsTemplateListItemType<ITEM>[];
   readonly onTemplateRemoved: PromiseVoidFunction;
   readonly removeConfirmationTitle: TranslatableText;
   readonly removeSuccessMessage: TranslatableText;
   readonly removeCommandCreator: <RESULT>(templateId: ExtractItemId<ITEM>) => Command<RESULT>;
};

export const FormsTemplatesListItemsRenderer = <ITEM extends Bean>(
   props: FormsTemplatesListItemsRendererProps<ITEM>
): JSX.Element => {
   const {
      activeItem,
      mode,
      setReadOnlyItem,
      setEditItem,
      enableSwitching,
      activeItemName,
   } = useFormsTemplatesContext<ITEM>();
   const { scrollContentToTop } = useFormsTemplatesScrollContext();

   useEffect(() => {
      if (mode === undefined) {
         setReadOnlyItem(props.items[0]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.items]);

   const handleItemClick = (item: FormsTemplateListItemType<ITEM>): void => {
      setReadOnlyItem(item);
      enableSwitching();
      scrollContentToTop();
   };

   const shouldDisplayActionItems = (item: FormsTemplateListItemType<ITEM>): boolean => {
      return (
         !isItemActive<ITEM>(activeItem, item) ||
         (isItemActive<ITEM>(activeItem, item) && mode !== 'EDIT' && item.id !== EMPTY_TEMPLATE_ID)
      );
   };

   return (
      <List.KeyExtractorRenderer<FormsTemplateListItemType<ITEM>>
         keyExtractor={(item) => item.id ?? EMPTY_TEMPLATE_ID}
         items={props.items}
      >
         {(item) => (
            <Anchor testId={'forActiveItem'} shouldPerformScroll={isItemActive(activeItem, item)}>
               {item.id === EMPTY_TEMPLATE_ID ? (
                  <FormsTemplatesListItem<ITEM>
                     testId={item.id ?? EMPTY_TEMPLATE_ID}
                     onClick={() => handleItemClick(item)}
                     active={isItemActive(activeItem, item)}
                     displayedName={isItemActive(activeItem, item) ? activeItemName : item.name}
                     displayActionItems={false}
                     onEditIconClick={() => setEditItem(item)}
                     templateId={EMPTY_TEMPLATE_ID}
                     onTemplateRemoved={props.onTemplateRemoved}
                     removeConfirmationTitle={props.removeConfirmationTitle}
                     removeSuccessMessage={props.removeSuccessMessage}
                     removeCommandCreator={props.removeCommandCreator}
                  />
               ) : (
                  <FormsTemplatesListItem<ITEM>
                     testId={item.id ?? EMPTY_TEMPLATE_ID}
                     onClick={() => handleItemClick(item)}
                     active={isItemActive(activeItem, item)}
                     displayedName={isItemActive(activeItem, item) ? activeItemName : item.name}
                     displayActionItems={shouldDisplayActionItems(item)}
                     onEditIconClick={() => setEditItem(item)}
                     templateId={item.id}
                     onTemplateRemoved={props.onTemplateRemoved}
                     removeConfirmationTitle={props.removeConfirmationTitle}
                     removeSuccessMessage={props.removeSuccessMessage}
                     removeCommandCreator={props.removeCommandCreator}
                  />
               )}
            </Anchor>
         )}
      </List.KeyExtractorRenderer>
   );
};
